'use client'

/**
 * THE AGREEMENT PAGE'S FIRM LANE: two session-lane groups (cutover note 01
 * E.1, E.2; research 01 §2.3.6).
 *
 *   - `cutoverAgreementViews`: where the firm's move stands (the page and the
 *     root banner read it), the administrator's saved match answers (decision
 *     041) and the agreement;
 *   - `cutoverMatcherViews`: the proposal the page resolves, read from the
 *     matcher's own group so the page and the server check the same thing.
 *
 * THE CONTRACTS ARE IMPORTED BY PATH, never through a leaf's barrel, which
 * publishes repositories that reach `@effect/sql-pg`.
 *
 * NO POLL. The status changes when support requests or runs the move, a few
 * times in a firm's life; a 5 minute freshness is enough for a banner, and
 * the agreement write sets the answer it returns straight into the cache.
 */

import {
  CutoverAgreementViewsApi,
} from '@/contexts/bms/cutover/agreement/presentation/agreement-views-api'
import { Effect } from 'effect'

import type {
  ClearGroupAnswersPayload,
  SaveMatchChoicePayload,
  SaveMatchChoicesPayload,
} from '@/contexts/bms/cutover/agreement/domain/match-draft'
import { CutoverMatcherViewsApi } from '@/contexts/bms/cutover/matcher/presentation/matcher-views-api'
import { ANSWERS_UNREADABLE_CODE } from '@/contexts/bms/cutover/agreement/domain/errors'
import { createHttpApiQuery } from '@/lib/api/http-api-query'
import { unwrapFiberFailureCause } from '@/lib/api/unwrap-fiber-failure'

import type { AgreeRequest, CutoverOnboardingTransport } from './transport'

export const cutoverAgreementEq = createHttpApiQuery(CutoverAgreementViewsApi)
export const cutoverMatcherEq = createHttpApiQuery(CutoverMatcherViewsApi)

/**
 * The organisation key a query uses before the identity has an org id. The
 * queries that use it are disabled until the real id arrives, so it is never
 * fetched.
 */
export const NO_ORG = '00000000-0000-4000-a000-000000000000'

export const CUTOVER_STATUS_STALE_MS = 5 * 60_000

export const cutoverAgreementKeys = {
  status: (organizationId: string) => ['bms.cutover.agreement.status', organizationId] as const,
  proposal: (organizationId: string) => ['bms.cutover.matcher.proposal', organizationId] as const,
  draft: (organizationId: string) => ['bms.cutover.agreement.draft', organizationId] as const,
}

export const cutoverStatusQueryOptions = (organizationId: string) =>
  cutoverAgreementEq.queryOptions({
    queryKey: cutoverAgreementKeys.status(organizationId),
    staleTime: CUTOVER_STATUS_STALE_MS,
    request: (client) => client.cutoverAgreementViews.getAgreementStatus({ path: { organizationId } }),
  })

/** The proposal is recomputed on every read; it is fresh while the page is open and never cached across visits. */
export const cutoverProposalQueryOptions = (organizationId: string) =>
  cutoverMatcherEq.queryOptions({
    queryKey: cutoverAgreementKeys.proposal(organizationId),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    request: (client) => client.cutoverMatcherViews.getMatchProposal({ path: { organizationId } }),
  })

/**
 * The administrator's saved match answers (decision 041), read once with the
 * proposal they are checked against. Like the proposal, no refetch on focus:
 * a fresh draft against a page's older proposal would read a colleague's
 * answers as changed.
 */
export const cutoverDraftQueryOptions = (organizationId: string) =>
  cutoverAgreementEq.queryOptions({
    queryKey: cutoverAgreementKeys.draft(organizationId),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    request: (client) => client.cutoverAgreementViews.getMatchDraft({ path: { organizationId } }),
  })

/** The firm's own Cova CRM search, run on submit or after a pause, never per keystroke. */
export const cutoverBookSearchQueryOptions = (organizationId: string, q: string) =>
  cutoverAgreementEq.queryOptions({
    queryKey: ['bms.cutover.agreement.searchBook', organizationId, q],
    staleTime: 30_000,
    request: (client) => client.cutoverAgreementViews.searchBookClients({ path: { organizationId }, urlParams: { q } }),
  })

/** The firm's other legacy clients, for "Keep together with". */
export const cutoverLegacySearchQueryOptions = (organizationId: string, q: string) =>
  cutoverAgreementEq.queryOptions({
    queryKey: ['bms.cutover.agreement.searchLegacy', organizationId, q],
    staleTime: 30_000,
    request: (client) => client.cutoverAgreementViews.searchLegacyClients({ path: { organizationId }, urlParams: { q } }),
  })

/**
 * THE FIRM LANE (build plan P2): the firm's own administrator, on the session
 * lane, its own firm in the path. The status key is the root banner's, so
 * agreeing here puts the banner away at once.
 */
export const firmTransport = (organizationId: string): CutoverOnboardingTransport => {
  const saveOne = cutoverAgreementEq.mutationOptions({
    mutationKey: ['bms.cutover.agreement.saveMatchChoice', organizationId],
    request: (client, { legacyId, payload }: { readonly legacyId: string; readonly payload: SaveMatchChoicePayload }) =>
      client.cutoverAgreementViews.saveMatchChoice({ path: { organizationId, legacyId }, payload }).pipe(Effect.as(legacyId)),
  })
  const clear = cutoverAgreementEq.mutationOptions({
    mutationKey: ['bms.cutover.agreement.clearMatchChoice', organizationId],
    request: (client, { legacyId }: { readonly legacyId: string }) =>
      client.cutoverAgreementViews.clearMatchChoice({ path: { organizationId, legacyId } }).pipe(Effect.as(legacyId)),
  })
  const saveBatch = cutoverAgreementEq.mutationOptions({
    mutationKey: ['bms.cutover.agreement.saveMatchChoices', organizationId],
    request: (client, payload: SaveMatchChoicesPayload) =>
      client.cutoverAgreementViews.saveMatchChoices({ path: { organizationId }, payload }),
  })
  const clearGroup = cutoverAgreementEq.mutationOptions({
    mutationKey: ['bms.cutover.agreement.clearGroupAnswers', organizationId],
    request: (client, payload: ClearGroupAnswersPayload) =>
      client.cutoverAgreementViews.clearGroupAnswers({ path: { organizationId }, payload }),
  })
  const prefill = cutoverAgreementEq.mutationOptions({
    mutationKey: ['bms.cutover.agreement.prefill', organizationId],
    request: (client, _: void) => client.cutoverAgreementViews.prefillFromLastAgreement({ path: { organizationId } }),
  })
  const agree = cutoverAgreementEq.mutationOptions({
    mutationKey: ['bms.cutover.agreement.agree', organizationId],
    request: (client, { payload }: AgreeRequest) => client.cutoverAgreementViews.agree({ path: { organizationId }, payload }),
  })
  return {
    viewer: 'firm',
    organizationId,
    needsInstruction: false,
    status: cutoverStatusQueryOptions(organizationId),
    proposal: cutoverProposalQueryOptions(organizationId),
    draft: cutoverDraftQueryOptions(organizationId),
    keys: {
      status: cutoverAgreementKeys.status(organizationId),
      proposal: cutoverAgreementKeys.proposal(organizationId),
      draft: cutoverAgreementKeys.draft(organizationId),
    },
    saveScope: `bms.cutover.agreement.draft.${organizationId}`,
    saveOne: saveOne.mutationFn,
    clear: clear.mutationFn,
    clearGroup: clearGroup.mutationFn,
    saveBatch: saveBatch.mutationFn,
    prefill: prefill.mutationFn,
    searchBook: (q) => cutoverBookSearchQueryOptions(organizationId, q),
    searchLegacy: (q) => cutoverLegacySearchQueryOptions(organizationId, q),
    agree: agree.mutationFn,
  }
}

/** The contract's error inside a refused call: `Effect.runPromise` rejects with a FiberFailure around it. */
const failureOf = (error: unknown): unknown => unwrapFiberFailureCause(error)

/** The message a refused call carries, from the BMS envelope, or a plain fallback. */
export const messageOf = (error: unknown, fallback: string): string => {
  const failure = failureOf(error)
  if (typeof failure === 'object' && failure !== null && 'message' in failure && typeof failure.message === 'string' && failure.message !== '') {
    return failure.message
  }
  return fallback
}

/** Whether a refused call was the 403 a non-administrator gets. */
export const isForbidden = (error: unknown): boolean => {
  const failure = failureOf(error)
  return typeof failure === 'object' && failure !== null && '_tag' in failure && failure._tag === 'BmsApiForbiddenError'
}

/**
 * WHY AN AGREEMENT WAS REFUSED, as the page acts on it (code review F8):
 *   - unreadable: the server could not read the saved answers (503,
 *     `ApiServiceUnavailableError` whose one detail's code is
 *     `ANSWERS_UNREADABLE_CODE`), nothing was agreed; the page keeps the
 *     confirmation and offers Try again;
 *   - unanswered: rows with no saved answer (400, `BmsApiValidationError`,
 *     each a detail `resolution.pairs.<legacyId>` / `unanswered`); the page
 *     names them;
 *   - other: any other refusal, in its own words.
 * The words are always the server's.
 */
export type AgreeRefusal =
  | { readonly kind: 'unreadable'; readonly message: string }
  | { readonly kind: 'unanswered'; readonly message: string; readonly legacyIds: ReadonlyArray<string> }
  | { readonly kind: 'other'; readonly message: string }

const PAIR_FIELD = 'resolution.pairs.'

export const agreeRefusalOf = (error: unknown, fallback: string): AgreeRefusal => {
  const failure = failureOf(error)
  const message = messageOf(error, fallback)
  const tag = typeof failure === 'object' && failure !== null && '_tag' in failure ? failure._tag : undefined
  const details =
    typeof failure === 'object' && failure !== null && 'details' in failure && Array.isArray(failure.details)
      ? (failure.details as ReadonlyArray<{ readonly field?: unknown; readonly code?: unknown; readonly message?: unknown }>)
      : []
  // Only the agreement's own 503 offers Try again: any other 503 (a session store) is said in its own words (round 4, nit 5).
  if (tag === 'ApiServiceUnavailableError' && details[0]?.code === ANSWERS_UNREADABLE_CODE) return { kind: 'unreadable', message }
  const legacyIds = details.flatMap((detail) =>
    detail.message === 'unanswered' && typeof detail.field === 'string' && detail.field.startsWith(PAIR_FIELD)
      ? [detail.field.slice(PAIR_FIELD.length)]
      : [],
  )
  if (tag === 'BmsApiValidationError' && legacyIds.length > 0) return { kind: 'unanswered', message, legacyIds }
  return { kind: 'other', message }
}
